export const STATUS_OPTIONS = [
  { value: "completed", label: "Active" },
  { value: "grace", label: "Grace Period" },
  { value: "paused", label: "Paused" },
  { value: "expired", label: "Expired" },
]

// Retention durations come from the server (quota.retentionOptions, driven by
// the plan feature matrix). Styling keyed by day with a neutral default.
const RETENTION_STYLES = {
  3:  { color: "border-blue-400",   activeBgColor: "border-blue-500 bg-blue-500",     textColor: "text-blue-500"   },
  7:  { color: "border-orange-400", activeBgColor: "border-orange-500 bg-orange-500", textColor: "text-orange-500" },
  30: { color: "border-green-400",  activeBgColor: "border-green-500 bg-green-500",   textColor: "text-green-500"  },
}
const RETENTION_STYLE_DEFAULT = { color: "border-primary/40", activeBgColor: "border-primary bg-primary", textColor: "text-primary" }
export const RETENTION_FALLBACK = [3, 7, 30]
export const buildRetentionOptions = (days) =>
  (Array.isArray(days) && days.length ? days : RETENTION_FALLBACK)
    .map((d) => ({ days: d, label: `${d} Days`, ...(RETENTION_STYLES[d] || RETENTION_STYLE_DEFAULT) }))

const SIZE_BAND_CREDITS = [
  { maxBytes: 10 * 1024 * 1024, base: 1 },
  { maxBytes: 100 * 1024 * 1024, base: 2 },
  { maxBytes: 1024 * 1024 * 1024, base: 3 },
  { maxBytes: 5 * 1024 * 1024 * 1024, base: 5 },
]

const RETENTION_MULTIPLIER = { 3: 1, 7: 2, 30: 5 }

export function calculateCost(fileSizeBytes, retentionDays) {
  const band =
    SIZE_BAND_CREDITS.find((b) => fileSizeBytes < b.maxBytes) ||
    SIZE_BAND_CREDITS[SIZE_BAND_CREDITS.length - 1]
  return band.base * (RETENTION_MULTIPLIER[retentionDays] || 1)
}

export function formatBytes(bytes) {
  if (!bytes) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

export function formatDate(dateStr) {
  if (!dateStr) return ""
  return new Date(dateStr).toLocaleDateString()
}

export function formatDateTime(dateStr) {
  if (!dateStr) return ""
  const d = new Date(dateStr)
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
}

export function formatNumber(n) {
  // Coerce first — String.prototype.toLocaleString is a no-op (no grouping),
  // so a count arriving as a string ("1234") would render without separators.
  const num = Number(n)
  if (!num) return "0"
  return num.toLocaleString()
}

export function formatRelativeTime(dateStr) {
  if (!dateStr) return ""
  const diffMs = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins} min ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days !== 1 ? "s" : ""} ago`
}

export function formatTimeRemaining(item) {
  if (!item || item.status === "expired") return null
  const now = new Date()
  const target =
    item.status === "grace"
      ? new Date(item.grace_expires_at)
      : new Date(item.expires_at)
  const diffMs = target - now
  if (diffMs <= 0) return null
  const oneDay = 1000 * 60 * 60 * 24
  // Round days up so a fresh 3-day share reads "3 days", not "2" (it's 2d 23h 59m).
  if (diffMs >= oneDay) {
    const days = Math.ceil(diffMs / oneDay)
    return { value: `${days} day${days !== 1 ? "s" : ""}`, suffix: "remaining" }
  }
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000)
  const totalMins = hours * 60 + minutes
  if (totalMins >= 60)
    return { value: `${Math.ceil(totalMins / 60)}h`, suffix: "remaining" }
  if (totalMins > 0)
    return {
      value: `${totalMins} min${totalMins !== 1 ? "s" : ""}`,
      suffix: "remaining",
    }
  return {
    value: `${seconds} sec${seconds !== 1 ? "s" : ""}`,
    suffix: "remaining",
  }
}
